import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import toast from "react-hot-toast";
import { formatDateToDDMMYYYY } from "./const";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { getUserData } from "./const";
import { createErpSocket } from "../utils/socketIo";
import Pagination from "../components/Pagination";
import {
  listBankAccounts,
  listCustomerUsers,
  isErpApiSuccess,
  postCustomerSettlement,
} from "../erpClient";
import { extractCustomerUsersArray } from "../utils/customerUsersErpAdapter";
import { hasPermission, PERMISSIONS } from "../utils/permissions";

// CSS styles for row colors
const settlementRowStyles = `
  .settlement-aavak-row {
    background-color: rgba(25, 135, 84, 0.03) !important;
    color: #000 !important;
  }
  .settlement-aavak-row td {
    background-color: rgba(25, 135, 84, 0.03) !important;
    color: #000 !important;
  }
  .settlement-jaavak-row {
    background-color: rgba(220, 53, 69, 0.03) !important;
    color: #000 !important;
  }
  .settlement-jaavak-row td {
    background-color: rgba(220, 53, 69, 0.03) !important;
    color: #000 !important;
  }
`;

// Inject CSS styles
if (typeof document !== "undefined") {
  const styleId = "settlement-row-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = settlementRowStyles;
    document.head.appendChild(style);
  }
}

const TRANSACTION_TYPE_OPTIONS = [
  { value: "wallet_recharge", label: "Wallet Recharge" },
  { value: "wallet_withdrawal", label: "Wallet Withdrawal" },
  { value: "jama", label: "Jama" },
  { value: "udhar", label: "Udhar Return" },
  { value: "refund", label: "Refund" },
  { value: "adjustment", label: "Adjustment" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "card", label: "Card" },
  { value: "wallet", label: "Wallet" },
];

const userData = getUserData();

function VoucherSlip() {
  const { buttonClass } = useButtonTableStyle();
  const [showAddModal, setShowAddModal] = useState(false);
  const [voucherDate, setVoucherDate] = useState("");
  const [userss, setUserss] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUserss, setLoadingUserss] = useState(false);
  const [transactionType, setTransactionType] = useState(null);
  const [paymentNature, setPaymentNature] = useState(""); // initially none selected
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [voucherSlips, setVoucherSlips] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loadingVoucherSlips, setLoadingVoucherSlips] = useState(false);
  const [socket, setSocket] = useState(null);
  const [usersDataMap, setUsersDataMap] = useState(new Map()); // Cache for user data

  const [canAddSettlement, setCanAddSettlement] = useState(() =>
    hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_ADD),
  );
  const [canEditSettlement, setCanEditSettlement] = useState(() =>
    hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_EDIT),
  );
  const [canViewSettlement, setCanViewSettlement] = useState(() =>
    hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_VIEW),
  );
  const [editingSlip, setEditingSlip] = useState(null);

  useEffect(() => {
    const syncPerms = () => {
      setCanAddSettlement(hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_ADD));
      setCanEditSettlement(hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_EDIT));
      setCanViewSettlement(hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_VIEW));
    };
    window.addEventListener("permissionsUpdated", syncPerms);
    window.addEventListener("erpAuthUpdated", syncPerms);
    return () => {
      window.removeEventListener("permissionsUpdated", syncPerms);
      window.removeEventListener("erpAuthUpdated", syncPerms);
    };
  }, []);

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 44,
      borderRadius: 8,
      borderColor: "#e0e6eb",
      borderWidth: "var(--bs-border-width)",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#e0e6eb",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0.25rem 0.65rem",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: 8,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 220,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
  };

  // Set today's date as default
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setVoucherDate(today);
  }, []);

  // Listen for API URL changes (data will refresh when modal opens)
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log("API URL changed in AddSettlement...", event.detail);
      // Data is lazy-loaded when modal opens, so just log the change
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  // Lazy-load users and bank accounts only when Add modal opens
  useEffect(() => {
    const fetchUserss = async () => {
      setLoadingUserss(true);
      try {
        const response = await listCustomerUsers({ search: "" });
        const rows = extractCustomerUsersArray(response);
        if (isErpApiSuccess(response) && rows?.length) {
          const usersMap = new Map();
          const options = rows.map((user) => {
            usersMap.set(String(user.id), user);
            return {
              value: user.id,
              label: `${user.name} (${user.mobile})`,
              ...user,
            };
          });
          setUserss(options);
          setUsersDataMap(usersMap);
        } else {
          setUserss([]);
        }
      } catch {
        setUserss([]);
      } finally {
        setLoadingUserss(false);
      }
    };
    const loadBankDetails = async () => {
      setLoadingBankAccounts(true);
      try {
        const response = await listBankAccounts();
        if (response?.success && Array.isArray(response.data?.bank_accounts)) {
          const fetchedBankDetails = response.data.bank_accounts.map(
            (bank) => ({
              id: bank.id,
              bankName: bank.bank_name || "",
              accountHolder: bank.holder_name || "",
              accountType: bank.account_type || "",
              ifscCode: bank.ifsc_code || bank.IFSC_code || "",
              accountNumber: bank.account_number || "",
              openingBalance: bank.opening_balance || "",
              balance:
                bank.available_balance !== null &&
                bank.available_balance !== undefined
                  ? bank.account_type === "metal"
                    ? `${bank.available_balance} gm`
                    : `₹${bank.available_balance}`
                  : bank.account_type === "metal"
                    ? "0 gm"
                    : "₹0",
              status: bank.status === 1 ? "Active" : "Inactive",
              statusClass:
                bank.status === 1
                  ? "bg-success-subtle text-success"
                  : "bg-danger-subtle text-danger",
              useForInvoice: bank.invoice === 1,
              useForPOS: bank.pos === 1,
            }),
          );
          fetchedBankDetails.sort((a, b) => b.id - a.id);
          const bankOptions = fetchedBankDetails.map((bank) => ({
            value: bank.id,
            label: bank.bankName,
            data: bank,
          }));
          setBankAccounts(bankOptions);
        } else {
          setBankAccounts([]);
        }
      } catch {
        setBankAccounts([]);
      } finally {
        setLoadingBankAccounts(false);
      }
    };
    if (showAddModal) {
      if (userss.length === 0) fetchUserss();
      if (bankAccounts.length === 0) loadBankDetails();
    }
  }, [showAddModal]);

  // Fetch bank accounts for dropdown
  const fetchBankDetails = useCallback(async () => {
    try {
      const response = await listBankAccounts();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance:
            bank.available_balance !== null &&
            bank.available_balance !== undefined
              ? bank.account_type === "metal"
                ? `${bank.available_balance} gm`
                : `₹${bank.available_balance}`
              : bank.account_type === "metal"
                ? "0 gm"
                : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.invoice === 1,
          useForPOS: bank.pos === 1,
        }));

        // Sort by ID in descending order (newest first)
        fetchedBankDetails.sort((a, b) => b.id - a.id);

        // Format data for React-Select dropdown
        const bankOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
          data: bank, // Keep original data for reference if needed
        }));

        setBankAccounts(bankOptions);
        // console.log("Bank accounts set successfully:", bankOptions);
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        toast("No bank accounts found");
      } else if (response && response.success === false) {
        setBankAccounts([]);
        toast.error(response.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      setBankAccounts([]);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch bank details",
      );
    }
  }, []);

  // Removed bank details auto-fetch on page load

  // Fetch voucher slips
  const fetchVoucherSlips = useCallback(async () => {
    setLoadingVoucherSlips(true);
    try {
      const data = await postCustomerSettlement({
        type: "pay_source",
        pay_source: "VOUCHERSLIP",
      });

      if (
        (isErpApiSuccess(data) || data?.message === "radha") &&
        data.data &&
        Array.isArray(data.data.payments)
      ) {
        const sorted = [...data.data.payments].sort((a, b) => {
          if (a.id && b.id) return b.id - a.id;
          if (a.date && b.date) return new Date(b.date) - new Date(a.date);
          return 0;
        });
        setVoucherSlips(sorted);
      } else {
        setVoucherSlips([]);
      }
    } catch (error) {
      setVoucherSlips([]);
    } finally {
      setLoadingVoucherSlips(false);
    }
  }, []);

  useEffect(() => {
    if (!canViewSettlement) return;
    const guardKey = "AddSettlement_voucher_fetch_guard";
    const lastTs = Number(sessionStorage.getItem(guardKey) || 0);
    const nowTs = Date.now();
    if (nowTs - lastTs < 500) {
      return;
    }
    sessionStorage.setItem(guardKey, String(nowTs));
    fetchVoucherSlips();
    setCurrentPage(1);
  }, [fetchVoucherSlips, canViewSettlement]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("AddSettlement: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("AddSettlement: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("AddSettlement: Socket connection error:", error);
    });

    const handlePaymentCreated = (data) => {
      if (!data?.payment) return;

      const newPayment = {
        id: data.payment.id,
        user_id: data.payment.user_id || data.payment.ref_id,
        customer_name: data.payment.customer_name || data.payment.user_name,
        amount: data.payment.amount || data.payment.amt,
        pay_mode: data.payment.pay_mode,
        remarks: data.payment.remarks || "",
        date: data.payment.date,
        created_at: data.payment.created_at,
        aavak_amt: data.payment.aavak_amt,
        jaavak_amt: data.payment.jaavak_amt,
      };

      setVoucherSlips((prev) => {
        // Check if payment already exists (avoid duplicates)
        const exists = prev.some((slip) => slip.id === newPayment.id);
        if (exists) {
          return prev.map((slip) =>
            slip.id === newPayment.id ? newPayment : slip,
          );
        }
        // Add new payment at the beginning (newest first)
        return [newPayment, ...prev].sort((a, b) => b.id - a.id);
      });
    };

    newSocket.on("payment_created", handlePaymentCreated);

    return () => {
      newSocket.off("payment_created", handlePaymentCreated);
      newSocket.disconnect();
      setSocket(null);
    };
  }, []);

  const handleOpenAdd = () => {
    if (!canAddSettlement) return;
    setEditingSlip(null);
    setVoucherDate(new Date().toISOString().split("T")[0]);
    setSelectedUser(null);
    setTransactionType(null);
    setPaymentNature("");
    setAmount("");
    setPaymentMode(null);
    setSelectedBankAccount(null);
    setRemarks("");
    setShowAddModal(true);
  };

  const handleOpenEdit = (slip) => {
    if (!canEditSettlement) return;
    setEditingSlip(slip);

    let dateStr = "";
    if (slip.date) {
      dateStr = slip.date.includes("T") ? slip.date.split("T")[0] : slip.date;
    } else if (slip.created_at) {
      dateStr = slip.created_at.includes("T")
        ? slip.created_at.split("T")[0]
        : slip.created_at.split(" ")[0];
    }
    setVoucherDate(dateStr || new Date().toISOString().split("T")[0]);

    const userId = slip.user_id || slip.ref_id;
    const userName = slip.customer_name || slip.user_name || "Customer";
    setSelectedUser(userId ? { value: userId, label: userName } : null);

    const isAavak =
      slip.paynature === 0 ||
      (slip.aavak_amt && parseFloat(slip.aavak_amt) > 0) ||
      slip.jaavak_amt === undefined ||
      slip.jaavak_amt === null ||
      parseFloat(slip.jaavak_amt) === 0;
    setPaymentNature(isAavak ? "credit" : "debit");

    const amt =
      slip.total_amount ||
      slip.amount ||
      slip.amt ||
      slip.aavak_amt ||
      slip.jaavak_amt ||
      "";
    setAmount(String(amt));

    const modeLabel = slip.pay_mode || "Cash";
    const modeOpt = PAYMENT_MODE_OPTIONS.find(
      (m) => m.label.toLowerCase() === modeLabel.toLowerCase(),
    );
    setPaymentMode(
      modeOpt || { value: modeLabel.toLowerCase(), label: modeLabel },
    );

    if (slip.bank_type || slip.bank_account_id) {
      setSelectedBankAccount({
        value: slip.bank_type || slip.bank_account_id,
        label:
          slip.bank_name ||
          `Bank Account #${slip.bank_type || slip.bank_account_id}`,
      });
    } else {
      setSelectedBankAccount(null);
    }

    if (slip.tnx_type) {
      const tnxOpt = TRANSACTION_TYPE_OPTIONS.find(
        (t) =>
          t.value === slip.tnx_type ||
          t.label.toLowerCase() === String(slip.tnx_type).toLowerCase(),
      );
      setTransactionType(
        tnxOpt || { value: slip.tnx_type, label: slip.tnx_type },
      );
    } else {
      setTransactionType(null);
    }

    setRemarks(slip.remarks || "");
    setShowAddModal(true);
  };

  const handleAddVoucherSlip = async (e) => {
    e.preventDefault();
    if (editingSlip && !canEditSettlement) {
      toast.error("You do not have permission to edit customer settlement.");
      return;
    }
    if (!editingSlip && !canAddSettlement) {
      toast.error("You do not have permission to add customer settlement.");
      return;
    }

    // Validation (optional, can be expanded)
    if (
      !selectedUser ||
      !paymentNature ||
      !amount ||
      !paymentMode ||
      !selectedBankAccount
    ) {
      toast.error("Please fill all required fields.");
      return;
    }
    let payload = {
      type: editingSlip ? "update" : "create",
      ...(editingSlip?.id ? { id: editingSlip.id } : {}),
      user_id: selectedUser.value,
      bank_type: selectedBankAccount.value,
      pay_mode: paymentMode.label,
      paynature: paymentNature === "credit" ? 0 : 1, // 0 for Aavak, 1 for Jaavak
      amount: amount,
      remarks: remarks,
      date: voucherDate,
      tnx_type: transactionType?.label || "",
      // by_user: userData?.id || "",
    };

    try {
      const data = await postCustomerSettlement(payload);
      if (isErpApiSuccess(data) || data?.message === "radha") {
        toast.success(
          editingSlip
            ? "Voucher slip updated successfully"
            : "Voucher slip added successfully",
        );
        setShowAddModal(false);
        setEditingSlip(null);
        // Reset form fields
        setVoucherDate(new Date().toISOString().split("T")[0]);
        setSelectedUser(null);
        setTransactionType(null);
        setPaymentNature("");
        setAmount("");
        setPaymentMode(null);
        setSelectedBankAccount(null);
        setRemarks("");
        // Refresh voucher slip list here
        fetchVoucherSlips();
        setCurrentPage(1); // Go to first page after add
      } else {
        toast.error(
          editingSlip
            ? "Failed to update voucher slip"
            : "Failed to add voucher slip",
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (editingSlip
            ? "Failed to update voucher slip"
            : "Failed to add voucher slip"),
      );
    }
  };

  // Pagination logic
  const displayedSlips = voucherSlips.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className="card w-100 position-relative overflow-hidden">
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Customer Settlement</h4>
            {canAddSettlement && (
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={handleOpenAdd}
              >
                Add Settlement
              </button>
            )}
          </div>
          <div className="card-body p-4">
            {/* Table placeholder */}
            {!canViewSettlement ? (
              <div className="text-center text-muted">
                You do not have permission to view customer settlements.
              </div>
            ) : loadingVoucherSlips ? (
              <div className="text-center text-muted">
                Loading voucher slips...
              </div>
            ) : voucherSlips.length === 0 ? (
              <div className="text-center text-muted">
                No voucher slips yet.
              </div>
            ) : (
              <>
                <div className="table-responsive mb-4 border rounded-1">
                  <table className="table text-nowrap mb-0 align-middle">
                    <thead className="text-dark fs-4">
                      <tr>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>Nature</th>
                        <th>Mode</th>
                        <th>Remarks</th>
                        {canEditSettlement && (
                          <th className="text-end">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {displayedSlips.map((slip, idx) => {
                        // Determine payment nature
                        const isAavak =
                          slip.paynature === 0 ||
                          (slip.aavak_amt && parseFloat(slip.aavak_amt) > 0) ||
                          slip.jaavak_amt === undefined ||
                          slip.jaavak_amt === null ||
                          parseFloat(slip.jaavak_amt) === 0;

                        return (
                          <tr
                            key={slip.id || idx}
                            className={
                              isAavak
                                ? "settlement-aavak-row"
                                : "settlement-jaavak-row"
                            }
                          >
                            <td>
                              {(() => {
                                let dateToFormat = null;

                                if (slip.created_at) {
                                  // Extract date part from datetime string
                                  dateToFormat = slip.created_at.includes("T")
                                    ? slip.created_at.split("T")[0]
                                    : slip.created_at.split(" ")[0];
                                } else if (slip.date) {
                                  dateToFormat = slip.date.includes("T")
                                    ? slip.date.split("T")[0]
                                    : slip.date;
                                }

                                if (dateToFormat) {
                                  try {
                                    // Convert to DD-MM-YYYY format
                                    const date = new Date(dateToFormat);
                                    if (!isNaN(date.getTime())) {
                                      return date
                                        .toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                        })
                                        .replace(/\//g, "-");
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Date formatting error:",
                                      error,
                                    );
                                  }
                                }
                                return "-";
                              })()}
                            </td>
                            <td>
                              {slip.user_name || slip.customer_name || "-"}
                            </td>
                            <td>
                              {slip.total_amount ||
                                slip.amount ||
                                slip.amt ||
                                slip.aavak_amt ||
                                slip.jaavak_amt ||
                                "-"}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  (slip.pay_type &&
                                    String(slip.pay_type).toUpperCase() ===
                                      "AAVAK") ||
                                  slip.paynature === 0 ||
                                  (slip.aavak_amt &&
                                    parseFloat(slip.aavak_amt) > 0) ||
                                  slip.jaavak_amt === undefined ||
                                  slip.jaavak_amt === null ||
                                  parseFloat(slip.jaavak_amt) === 0
                                    ? "bg-success-subtle text-success"
                                    : "bg-danger-subtle text-danger"
                                }`}
                              >
                                {(slip.pay_type &&
                                  String(slip.pay_type).toUpperCase() ===
                                    "AAVAK") ||
                                slip.paynature === 0 ||
                                (slip.aavak_amt &&
                                  parseFloat(slip.aavak_amt) > 0) ||
                                slip.jaavak_amt === undefined ||
                                slip.jaavak_amt === null ||
                                parseFloat(slip.jaavak_amt) === 0
                                  ? "AAVAK"
                                  : "JAAVAK"}
                              </span>
                            </td>
                            <td>{slip.pay_mode || "-"}</td>
                            <td>{slip.remarks || "-"}</td>
                            {canEditSettlement && (
                              <td className="text-end">
                                <button
                                  type="button"
                                  title="Edit Settlement"
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => handleOpenEdit(slip)}
                                >
                                  <iconify-icon
                                    icon="solar:pen-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  totalItems={voucherSlips.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Voucher Slip Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setEditingSlip(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingSlip ? "Edit Settlement" : "Add Settlement"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddVoucherSlip}>
            <Row className="gy-3 mb-3">
              <Col xs={12} md={6}>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={voucherDate}
                  onChange={(e) => setVoucherDate(e.target.value)}
                  required
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label>Customer</Form.Label>
                <Select
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  options={userss}
                  value={selectedUser}
                  onChange={setSelectedUser}
                  isLoading={loadingUserss}
                  placeholder="Select Customer"
                  isSearchable
                  required
                />
              </Col>
            </Row>
            <Row className="gy-3 mb-3">
              <Col xs={12} md={6}>
                <Form.Label>Transaction Type</Form.Label>
                <CreatableSelect
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  options={TRANSACTION_TYPE_OPTIONS}
                  value={transactionType}
                  onChange={setTransactionType}
                  placeholder="Select or type..."
                  isClearable
                  required
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  required
                />
              </Col>
            </Row>
            <Row className="gy-3 mb-3">
              <Col
                xs={12}
                md={6}
                className="d-flex flex-column justify-content-end"
              >
                <Form.Label className="me-3">Payment Nature</Form.Label>
                <div className="d-flex gap-4">
                  <Form.Check
                    type="radio"
                    id="credit"
                    label="Aavak"
                    name="paymentNature"
                    value="credit"
                    checked={paymentNature === "credit"}
                    onChange={() => setPaymentNature("credit")}
                  />
                  <Form.Check
                    type="radio"
                    id="debit"
                    label="Jaavak"
                    name="paymentNature"
                    value="debit"
                    checked={paymentNature === "debit"}
                    onChange={() => setPaymentNature("debit")}
                  />
                </div>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label>Payment Mode</Form.Label>
                <Select
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  options={PAYMENT_MODE_OPTIONS}
                  value={paymentMode}
                  onChange={setPaymentMode}
                  placeholder="Select Payment Mode"
                  isClearable
                  required
                />
              </Col>
            </Row>
            <Row className="gy-3 mb-3">
              <Col xs={12} md={6}>
                <Form.Label>Received In / Paid From</Form.Label>
                <Select
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  options={bankAccounts}
                  value={selectedBankAccount}
                  onChange={setSelectedBankAccount}
                  isLoading={loadingBankAccounts}
                  placeholder="Select Bank Account"
                  isClearable
                  required
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={1}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter remarks"
                />
              </Col>
            </Row>
            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <Button type="submit" variant="primary" className={buttonClass}>
                {editingSlip ? "Update" : "Submit"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default VoucherSlip;
